import { Injectable } from '@angular/core';
import { Observable, OperatorFunction } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ShareRequest, ShowIfHttpError } from '@certemy/common';

import { HttpService } from 'services/http/http.service';
import {
  Organization,
  OrganizationGroup,
  OrganizationRegistry,
  OrganizationSystemModule,
  PublicRegistry,
} from 'services/organization/organization.interface';
import {
  buildQueryParams,
  convertFilterStructToQueryParam,
  createFileUploadHeaders,
  createQueryParam,
} from 'utils/helpers';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { CustomFields } from 'features/custom-fields';
import { OrganizationCustomField } from 'features/custom-fields/custom-fields.interface';
import { ORGANIZATION_TYPE } from 'constants/constants';
import { GENERATE_REPORT_RESPONSE_TYPE } from 'features/report-generate/report-generate.constants';
import {
  BatchRegistryUploadResponse,
  BatchRegistryUploadRow,
} from 'features/organization-settings/registry/batch-registry-upload/batch-registry-upload.model';
import { SmartForm } from '@certemy/ui-components';

@Injectable()
export class OrganizationService {
  private profile;

  constructor(private http: HttpService, private store: Store<any>) {
    this.store.select(Selectors.currentProfile).subscribe((profile) => (this.profile = profile));
  }

  getFlatCustomFields(): Observable<OrganizationCustomField[]> {
    // Returns all organization custom fields (single and from groups) in a flattened array.
    return this.http.get(`/organization/custom_fields`);
  }

  @ShowIfHttpError('Error retrieving custom fields.')
  getFlatCustomFieldsForOrg(orgId: number): Observable<OrganizationCustomField[]> {
    // Returns all organization custom fields (single and from groups) in a flattened array.
    return this.http.get(`/organization/custom-fields/${orgId}`);
  }

  deleteEntityType(id) {
    return this.http.delete(`/entity_types/${id}`);
  }
  getDetailsEntityType(id) {
    return this.http.get(`/entity_types/${id}`);
  }

  updateEntityType(method: string, params: any) {
    return this.http[method](`/entity_types/${params.id ? params.id : ''}`, params);
  }

  getEntityType(obj: any = {}) {
    const params = [
      createQueryParam('page', obj.count || 1),
      createQueryParam('order_id', obj.field),
      createQueryParam('order_type', obj.sort),
      createQueryParam('search', obj.search),
      createQueryParam('filters', { status_id: obj.filters }),
    ];
    const queryParams = buildQueryParams(params);
    return this.http.get(`/entity_types${queryParams}`);
  }

  getOrganizationInformation() {
    const organizationId: number = this.profile.organization_id;
    return this.http.get(`/organization/${organizationId}/information`);
  }

  changeOrganizationInformation(body) {
    const organizationId: number = this.profile.organization_id;
    return this.http.put(`/organization/${organizationId}/information`, body);
  }

  getOrganization(organizationId: number = this.profile.organization_id) {
    return this.http.get(`/organization/${organizationId}`);
  }

  getOrganizationType(organizationId: number = this.profile.organization_id) {
    return this.http.get(`/organization/${organizationId}/type`);
  }

  setOrganizationType(
    organizationId: number = this.profile.organization_id,
    organizationTypeId: ORGANIZATION_TYPE,
    isTestOrganization: boolean,
  ) {
    return this.http.post(`/organization/${organizationId}/type`, {
      organization_type_id: organizationTypeId,
      is_test_organization: isTestOrganization,
    });
  }

  @ShowIfHttpError('Cannot rename organization.')
  renameOrganization(organizationId: number = this.profile.organization_id, organizationName: string) {
    return this.http.patch(`/organization/${organizationId}/name`, {
      name: organizationName,
    });
  }

  @ShowIfHttpError('Cannot receive organization.')
  getOrganizationList(profile_id: number, search?: string): Observable<Organization[]> {
    const queryParams = convertFilterStructToQueryParam({ profile_id, search });

    return this.http.get(`/organization?${queryParams}`).pipe(map((data) => data.result));
  }

  @ShowIfHttpError('Cannot receive connected organization.')
  getConnectedOrganization(id: number): Observable<Organization[]> {
    return this.http.get(`/organization/${id}/employer/connected_organization`);
  }

  createOrganization(name): Observable<{ id: number }> {
    return this.http.post('/user/organization', { name });
  }

  @ShareRequest
  @ShowIfHttpError('Cannot receive custom field groups.')
  getCustomFieldGroups(): Observable<CustomFields.Group[]> {
    return this.http.get(`/organization/custom_field_selections`).pipe(map((res) => res.result.groups));
  }

  @ShowIfHttpError('Cannot receive custom field groups.')
  getCustomFieldGroupsForSpecificOrganization(organizationId: number): Observable<CustomFields.Group[]> {
    return this.http
      .get(`/organization/custom_field_selections/${organizationId}`)
      .pipe(map((res) => res.result.groups));
  }

  getOrganizationSystemModules(): Observable<OrganizationSystemModule.ResponseData[]> {
    return this.http.get(`/organization/system_modules`);
  }

  getOrganizationGroups(): Observable<OrganizationGroup.ResponseData[]> {
    return this.http.get(`/organization/groups`);
  }

  getOrganizationRegistry(): Observable<OrganizationRegistry.ResponseData> {
    return this.http.get(`/organization/registry`).pipe(map((data) => data.result));
  }

  @ShowIfHttpError('An error occurred while verifing profile. Please, try again.')
  verifyProfile(
    organizationId: number,
    profileId: number,
  ): Observable<{ verify: boolean; profile: string; organization: string }> {
    return this.http.get(`/organization/${organizationId}/verify/profile/${profileId}`);
  }

  @ShowIfHttpError('An error occurred while updating Registry. Please, try again.')
  updateOrganizationRegistry(data: OrganizationRegistry.UpdateData) {
    return this.http.put(`/organization/registry`, data);
  }

  @ShowIfHttpError('An error occurred while save as draft Registry. Please, try again.')
  saveAsDraftOrganizationRegistry(data: OrganizationRegistry.SaveData): Observable<OrganizationRegistry.ResponseData> {
    return this.http.post(`/registry/settings/draft`, data);
  }

  @ShowIfHttpError('An error occurred while publish Registry. Please, try again.')
  publishOrganizationRegistry(data: OrganizationRegistry.SaveData): Observable<OrganizationRegistry.ResponseData> {
    return this.http.post(`/registry/settings/draft/publish`, data);
  }

  @ShowIfHttpError('Cannot receive public registry credentials')
  getPublicRegistryCredentials(registryId: string, query): Observable<PublicRegistry.Credential[]> {
    const queryParams = this.getQueryParams(query as any);
    return this.http.get(`/registry/public/${registryId}/certifications${queryParams}`);
  }

  @ShowIfHttpError('Cannot receive registry. Please try again later.')
  getPublicOrganizationRegistry(
    registryId: string,
    requestParams: PublicRegistry.QueryParams,
  ): Observable<PublicRegistry.ResponseRowData> {
    const query = this.getQueryParams(requestParams as any);
    return this.publicOrganizationRegistryRequest(registryId, query);
  }

  @ShowIfHttpError('Cannot receive preview registry. Please try again later.')
  getPreviewOrganizationRegistry(
    registryId: string,
    requestParams: PublicRegistry.QueryParams,
  ): Observable<PublicRegistry.ResponseRowData> {
    const query = this.getQueryParams(requestParams as any);
    return this.previewOrganizationRegistryRequest(registryId, query);
  }

  @ShowIfHttpError('Cannot receive board list. Please try again later.')
  getBoardList(registryId: string): Observable<SmartForm.FieldOption[]> {
    return this.http.get(`/registry/public/${registryId}/child-organizations`);
  }

  @ShowIfHttpError('Cannot receive registry. Please try again later.')
  getPublicOrganizationRegistryByProfileId(
    registryId: string,
    profile_id: number,
  ): Observable<PublicRegistry.ResponseRowData> {
    const query = buildQueryParams([
      {
        name: 'profile_id',
        value: profile_id,
      },
    ]);
    return this.publicOrganizationRegistryRequest(registryId, query).pipe(
      this.disciplinaryActionColumnProcessingPipe(),
    );
  }

  @ShowIfHttpError('Cannot receive preview registry. Please try again later.')
  getPreviewOrganizationRegistryByProfileId(
    registryId: string,
    profile_id: number,
  ): Observable<PublicRegistry.ResponseRowData> {
    const query = buildQueryParams([
      {
        name: 'profile_id',
        value: profile_id,
      },
    ]);
    return this.previewOrganizationRegistryRequest(registryId, query).pipe(
      this.disciplinaryActionColumnProcessingPipe(),
    );
  }

  private publicOrganizationRegistryRequest(registryId: string, query: string) {
    return this.http.get(`/organization/public_registry/${registryId}${query}`);
  }

  private previewOrganizationRegistryRequest(registryId: string, query: string) {
    return this.http.get(`/organization/public-registry/preview/${registryId}${query}`);
  }

  private disciplinaryActionColumnProcessingPipe(): OperatorFunction<
    PublicRegistry.ResponseRowData,
    PublicRegistry.ResponseRowData
  > {
    return map((response) => {
      if (!response || !response.count) {
        return response;
      }
      return {
        ...response,
        result: response.result.map((record) => ({
          ...record,
          rows: record.rows.map((row) => {
            if (
              ['disciplinary_action'].includes(row.sort_id) &&
              row.url &&
              (!row.value || (row.value as string).toLowerCase() === 'no')
            ) {
              return {
                ...row,
                url: null,
              };
            }
            return row;
          }),
        })),
      };
    });
  }

  @ShowIfHttpError('Cannot receive registry item. Please try again later.')
  getPublicRegistryItemByNumberInstanceId(
    registryId: string,
    numberInstanceId: number,
  ): Observable<PublicRegistry.ResponseRowData> {
    return this.http
      .get(`/organization/public_registry/${registryId}/number_instance_id/${numberInstanceId}`)
      .pipe(this.disciplinaryActionColumnProcessingPipe());
  }

  @ShowIfHttpError('Cannot receive preview registry item. Please try again later.')
  getPreviewRegistryItemByNumberInstanceId(
    registryId: string,
    numberInstanceId: number,
  ): Observable<PublicRegistry.ResponseRowData> {
    return this.http
      .get(`/organization/public-registry/preview/${registryId}/number_instance_id/${numberInstanceId}`)
      .pipe(this.disciplinaryActionColumnProcessingPipe());
  }

  @ShowIfHttpError('Cannot connect stripe account. Please try again later.')
  connectStripeAccount(body) {
    return this.http
      .post(`/organization/connect-stripe-account`, body)
      .pipe(tap(() => this.http.showSuccessMessage('You have successfully connected stripe account.')));
  }

  @ShowIfHttpError('An error occurred while updating Registry. Please, try again.')
  addRegistryFilter(data: OrganizationRegistry.RegistryFilters) {
    return this.http.put(`/registry/settings`, data);
  }

  @ShowIfHttpError('Cannot batch upload registry.')
  batchUploadRegistries(
    organizationTypeId: ORGANIZATION_TYPE,
    file: File,
    allowSignUp: boolean,
  ): Observable<BatchRegistryUploadResponse> {
    const formData: FormData = new FormData();
    formData.append('files', file, file.name);
    if (allowSignUp) {
      formData.append('allowSignUp', `${allowSignUp}`);
    }
    const options = createFileUploadHeaders();
    const url =
      organizationTypeId === ORGANIZATION_TYPE.EMPLOYER
        ? '/certification-bulk/file-upload/employer'
        : '/certification-bulk/file-upload/board';
    return this.http.post(url, formData, options);
  }

  @ShowIfHttpError('Cannot resubmit registry corrections.')
  resubmitRegistryCorrections(
    body: {
      rows: BatchRegistryUploadRow[];
      logGroupId: number;
      allowSignUp?: boolean;
    },
    organizationTypeId: ORGANIZATION_TYPE,
  ): Observable<BatchRegistryUploadResponse> {
    const url =
      organizationTypeId === ORGANIZATION_TYPE.EMPLOYER
        ? '/certification-bulk/certification-numbers/employer'
        : '/certification-bulk/certification-numbers/board';
    return this.http.post(url, body);
  }

  @ShowIfHttpError('Cannot receive batch registry upload data.')
  getBatchUploadLogs(logGroupId: number): Observable<BatchRegistryUploadRow[]> {
    const params = [createQueryParam('logGroupId', logGroupId), createQueryParam('onlyErrors', 0)];
    const queryParams = buildQueryParams(params);

    return this.http
      .get<{ logRows: BatchRegistryUploadRow[] }>(`/certification-bulk/certification-number-log${queryParams}`)
      .pipe(map((res) => res.logRows));
  }

  @ShowIfHttpError('An error occurred while getting batch upload sample file.')
  getBatchUploadFile(
    organizationTypeId: ORGANIZATION_TYPE,
    responseType = GENERATE_REPORT_RESPONSE_TYPE.ARRAY_BUFFER,
  ): Observable<ArrayBuffer> {
    const url =
      organizationTypeId === ORGANIZATION_TYPE.EMPLOYER
        ? '/certification-bulk/example-spreadsheet/employer'
        : '/certification-bulk/example-spreadsheet/board';
    return this.http.get(url, { responseType });
  }

  private getQueryParams({
    search,
    filters,
    page,
    page_size,
    order_id,
    order_type,
    limit,
  }: PublicRegistry.QueryParams): string {
    return buildQueryParams(
      [
        search && createQueryParam('search', search),
        filters && createQueryParam('filters', filters),
        page && createQueryParam('page', page),
        page_size && createQueryParam('page_size', page_size),
        order_id && createQueryParam('order_id', order_id),
        order_type && createQueryParam('order_type', order_type),
        limit && createQueryParam('limit', limit),
      ].filter(Boolean),
    );
  }
}
